export const FULL_SERVICE_PRICE = 3.99;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

export const normalizeBooleanFlag = (value) =>
  value === true ||
  value === 1 ||
  ["true", "1", "yes"].includes(String(value || "").toLowerCase());

export const isIncludedGroupServicePart = (item) => {
  const value = item?.value ?? item;
  return (
    value?.type === "part" &&
    normalizeBooleanFlag(value?.includedInGroupService)
  );
};

export const calculatePaymentAmounts = ({
  amountDue = 0,
  amountPaid = 0,
  previousPaid = 0,
  totalPrice = 0,
} = {}) => {
  const due = Math.max(0, round2(amountDue));
  const appliedPayment = Math.min(due, Math.max(0, round2(amountPaid)));
  const remaining = round2(Math.max(0, due - appliedPayment));
  const cumulativePaid = round2(
    Math.max(0, Number(previousPaid) || 0) + appliedPayment,
  );
  const total = Math.max(0, Number(totalPrice) || 0);
  const paid = total > 0 ? Math.min(total, cumulativePaid) : cumulativePaid;
  const status =
    remaining === 0 ? "Paid" : paid > 0 ? "Partialy Paid" : "Unpaid";

  return { amountPaid: appliedPayment, paid, remaining, status };
};

export const normalizeReceiptLine = (item) => {
  const v = item?.value ?? item;
  const qty = Number(v?.quantity) || 1;
  const unitPrice = Number(v?.price) || 0;
  const isTireSvc = v?.type === "tire_service";
  const isIncludedGroupPart = isIncludedGroupServicePart(v);
  const baseTotal = isIncludedGroupPart
    ? 0
    : isTireSvc
      ? round2(unitPrice * qty)
      : Number(v?.total_price) || round2(unitPrice * qty);
  const fee = isTireSvc ? round2(baseTotal * 0.02) : 0;
  const lineTotal = isTireSvc ? round2(baseTotal + fee) : round2(baseTotal);
  const next = {
    ...v,
    disposable_fee: isTireSvc ? fee : 0,
    total_price: lineTotal,
  };

  return item?.value ? { ...item, value: next } : next;
};

export const normalizeOilChangeState = (source = {}) => {
  const hasOilChangeData = Boolean(
    source?.oil ||
      source?.oilFilter ||
      source?.extraOilSelection ||
      source?.fullServiceIncluded ||
      (Array.isArray(source?.oilTechnician) && source.oilTechnician.length > 0) ||
      Number(source?.oilPrice || 0) > 0 ||
      Number(source?.extraOilPrice || 0) > 0 ||
      Number(source?.oilFilterPrice || 0) > 0 ||
      Number(source?.fullServicePrice || 0) > 0,
  );

  return {
    ...source,
    oilChangeAdded: source?.oilChangeAdded || hasOilChangeData,
  };
};

export const computeReceiptTotals = ({
  services = [],
  tires = [],
  oilPrice = 0,
  extraOilPrice = 0,
  oilFilterPrice = 0,
  fullServiceIncluded = false,
  fullServicePrice = 0,
  taxIncluded = false,
  taxPct = 0,
  discountIncluded = false,
  discountType = "",
  discount = 0,
  paid = 0,
} = {}) => {
  const normServices = services.map(normalizeReceiptLine);
  const normTires = tires.map(normalizeReceiptLine);
  let subtotal = 0;
  let taxable = 0;
  const all = [
    ...normServices.map((s) => s.value ?? s),
    ...normTires.map((t) => t.value ?? t),
  ];

  all.forEach((it) => {
    const total = Number(it.total_price) || 0;
    subtotal += total;
    const isTaxable = (it.tax ?? "Taxable") === "Taxable";
    if (isTaxable) taxable += total;
  });

  const fullServiceTotal = fullServiceIncluded
    ? round2((Number(fullServicePrice) || 0) || FULL_SERVICE_PRICE)
    : 0;
  const oilExtras = round2(
    (Number(oilPrice) || 0) +
      (Number(extraOilPrice) || 0) +
      (Number(oilFilterPrice) || 0) +
      fullServiceTotal,
  );

  subtotal += oilExtras;
  taxable += oilExtras;

  const taxEnabled = normalizeBooleanFlag(taxIncluded);
  const discountEnabled = normalizeBooleanFlag(discountIncluded);
  const discountValue = Number(discount) || 0;
  const discountAmount =
    discountEnabled && discountValue > 0
      ? round2(
          String(discountType).toLowerCase() === "percentage"
            ? subtotal * (discountValue / 100)
            : discountValue,
        )
      : 0;
  const appliedDiscount = Math.min(discountAmount, subtotal);
  const taxableAfterDiscount = Math.max(0, taxable - appliedDiscount);
  const totalBeforeTax = Math.max(0, subtotal - appliedDiscount);

  const tax = taxEnabled
    ? round2(((Number(taxPct) || 0) / 100) * taxableAfterDiscount)
    : 0;
  const totalPrice = round2(totalBeforeTax + tax);
  const remaining = round2(totalPrice - (Number(paid) || 0));

  return {
    services: normServices,
    tires: normTires,
    subtotal,
    discountAmount: appliedDiscount,
    tax,
    totalPrice,
    remaining,
  };
};

export const computeReceiptWithFormattedTotals = (source = {}) => {
  const normalizedSource = normalizeOilChangeState(source);
  const fullServicePrice = normalizedSource.fullServiceIncluded
    ? round2((Number(normalizedSource.fullServicePrice) || 0) || FULL_SERVICE_PRICE)
    : 0;
  const next = {
    ...normalizedSource,
    fullServicePrice,
  };

  const { subtotal, discountAmount, tax, totalPrice, remaining, services, tires } =
    computeReceiptTotals({
      services: next.services || [],
      tires: next.tires || [],
      oilPrice: next.oilPrice,
      extraOilPrice: next.extraOilPrice,
      oilFilterPrice: next.oilFilterPrice,
      fullServiceIncluded: next.fullServiceIncluded,
      fullServicePrice: next.fullServicePrice,
      taxIncluded: next.taxIncluded,
      taxPct: next.taxType,
      discountIncluded: next.discountIncluded,
      discountType: next.discountType,
      discount: next.discount,
      paid: next.paid,
    });

  return {
    ...next,
    services,
    tires,
    subTotal: subtotal.toFixed(2),
    subtotal: subtotal.toFixed(2),
    discountAmount: discountAmount.toFixed(2),
    tax: tax.toFixed(2),
    totalPrice: totalPrice.toFixed(2),
    remaining: remaining.toFixed(2),
  };
};
